import type { CoinApi } from "../api/CoinApi";
import type { ListSolutionsParams, SolutionDto } from "../api/types";
import { NoSolutionsAvailableError } from "../playback/TourSource";
import type { NextTourOptions, TourData, TourSource } from "../playback/TourSource";

export type TourStrategy = "auto" | "latest" | "most_found" | "random" | "open" | "closed";

export interface ApiTourSourceOptions {
  strategy?: TourStrategy;
  /** Size of the small in-memory queue refilled from the API; never the whole database. */
  pageSize?: number;
}

const DEFAULT_PAGE_SIZE = 8;
const RANDOM_POOL_SIZE = 30;

function toTourData(dto: SolutionDto): TourData {
  return {
    id: dto.solution_hash,
    tour: dto.tour,
    isClosed: dto.is_closed,
    score: dto.score,
    timesFound: dto.times_found,
    firstGeneration: dto.first_generation,
    lastGeneration: dto.last_generation,
    representedCount: dto.represented_count,
  };
}

function shuffle<T>(items: T[]): T[] {
  const copy = items.slice();
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/**
 * Live `TourSource` backed by `GET /solutions`. Keeps only a small
 * in-memory queue (section 21: "do not download the entire database"),
 * refilling it from the API whenever it runs dry. The API's own
 * `sort`/`is_closed` filters do the real work; this class just decides
 * which query to issue for the configured strategy and keeps the queue
 * from replaying the same solution twice in a row.
 */
export class ApiTourSource implements TourSource {
  private queue: SolutionDto[] = [];
  private recentIds: string[] = [];
  private readonly strategy: TourStrategy;
  private readonly pageSize: number;

  constructor(private readonly api: CoinApi, options: ApiTourSourceOptions = {}) {
    this.strategy = options.strategy ?? "auto";
    this.pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  }

  async next(options: NextTourOptions = {}): Promise<TourData> {
    const { excludeId, continueFromSquare } = options;

    if (continueFromSquare !== undefined) {
      const continuation = await this.api.getContinuation(continueFromSquare, { openOnly: true });
      if (continuation && !continuation.is_closed) return toTourData(continuation);
    }

    if (this.queue.length === 0) {
      await this.refill();
    }
    if (this.queue.length === 0) {
      throw new NoSolutionsAvailableError();
    }

    let candidate = this.queue.shift()!;
    if ((candidate.solution_hash === excludeId || this.recentIds.includes(candidate.solution_hash)) && this.queue.length > 0) {
      // Don't replay the tour that just finished — rotate it to the back
      // and take the next one instead.
      this.queue.push(candidate);
      const freshIndex = this.queue.findIndex(item => item.solution_hash !== excludeId && !this.recentIds.includes(item.solution_hash));
      candidate = freshIndex >= 0 ? this.queue.splice(freshIndex, 1)[0] : this.queue.shift()!;
    }
    this.recentIds.push(candidate.solution_hash);
    if (this.recentIds.length > Math.max(3, this.pageSize - 1)) this.recentIds.shift();
    return toTourData(candidate);
  }

  private async refill(): Promise<void> {
    for (const params of this.candidateQueries()) {
      const items = await this.fetchPage(params);
      if (items.length > 0) {
        this.queue = shuffle(items);
        return;
      }
    }
    this.queue = [];
  }

  /**
   * Default selection strategy ("auto"): prefer open tours and use closed
   * tours only when no open result exists. The other named strategies map directly onto
   * the API's own sort/filter parameters; "random" pulls a larger pool and
   * shuffles client-side, since the API does not offer server-side random
   * ordering.
   */
  private candidateQueries(): ListSolutionsParams[] {
    switch (this.strategy) {
      case "open":
        return [{ isClosed: false, sort: "last_seen", order: "desc" }];
      case "closed":
        return [{ isClosed: true, sort: "last_seen", order: "desc" }];
      case "latest":
        return [{ sort: "last_seen", order: "desc" }];
      case "most_found":
        return [{ sort: "times_found", order: "desc" }];
      case "random":
        return [{ sort: "last_seen", order: "desc", limit: RANDOM_POOL_SIZE }];
      case "auto":
      default:
        return [
          { isClosed: false, sort: "last_seen", order: "desc" },
          { isClosed: true, sort: "last_seen", order: "desc" },
        ];
    }
  }

  private async fetchPage(params: ListSolutionsParams): Promise<SolutionDto[]> {
    const response = await this.api.listSolutions({ limit: this.pageSize, ...params });
    return response.items;
  }
}
